import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import { IdentityDetailComponent } from '../detail/identity-detail.component';

interface IdentityItem {
  id: string;
  name: string;
  type: string;
  avatarId: string;
  isDefault: boolean;
  avatarUrl?: string;
}

@Component({
  selector: 'identity-list',
  templateUrl: './identity-list.component.html',
  styleUrls: ['./identity-list.component.less'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    FormsModule,
    NzAvatarModule,
    NzButtonModule,
    NzCardModule,
    NzInputModule,
    NzTableModule,
    NzTagModule,
    NzSelectModule,
    NzPopconfirmModule,
    NzIconModule,
    NzSpinModule,
    NzModalModule,
    NzPaginationModule,
    NzDividerModule,
    NzDropDownModule,
    NzToolTipModule,
    I18nPipe,
    TestIdDirective
  ]
})
export class IdentityListComponent implements OnInit {
  @Input() serviceNumberId!: string;
  // 列表数据
  listOfData: IdentityItem[] = [];
  // 加载状态
  loading = false;
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  // TestId properties for template binding
  'identity-list-container' = 'identity-list-container';
  'identity-list-header' = 'identity-list-header';
  'identity-list-actions' = 'identity-list-actions';
  'refresh-button' = 'refresh-button';
  'refresh-icon' = 'refresh-icon';
  'refresh-text' = 'refresh-text';
  'add-identity-button' = 'add-identity-button';
  'add-identity-icon' = 'add-identity-icon';
  'add-identity-text' = 'add-identity-text';
  'identity-list-content' = 'identity-list-content';
  'identity-table' = 'identity-table';
  'identity-table-head' = 'identity-table-head';
  'identity-table-header-row' = 'identity-table-header-row';
  'identity-avatar-header' = 'identity-avatar-header';
  'identity-name-header' = 'identity-name-header';
  'identity-type-header' = 'identity-type-header';
  'identity-default-header' = 'identity-default-header';
  'identity-actions-header' = 'identity-actions-header';
  'identity-table-body' = 'identity-table-body';
  'identity-table-row' = 'identity-table-row';
  'identity-avatar-cell' = 'identity-avatar-cell';
  'identity-avatar' = 'identity-avatar';
  'identity-name-cell' = 'identity-name-cell';
  'identity-type-cell' = 'identity-type-cell';
  'identity-type-tag' = 'identity-type-tag';
  'identity-default-cell' = 'identity-default-cell';
  'identity-default-tag' = 'identity-default-tag';
  'identity-actions-cell' = 'identity-actions-cell';
  'edit-identity-button' = 'edit-identity-button';
  'edit-identity-icon' = 'edit-identity-icon';
  'actions-divider-1' = 'actions-divider-1';
  'set-default-button' = 'set-default-button';
  'set-default-icon' = 'set-default-icon';
  'actions-divider-2' = 'actions-divider-2';
  'delete-identity-button' = 'delete-identity-button';
  'delete-identity-icon' = 'delete-identity-icon';

  constructor(
    private readonly httpService: HttpService,
    private readonly imageService: ImageService, // 注入 ImageService
    private readonly message: NzMessageService,
    private readonly modal: NzModalService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  // 加载数据
  loadData(): void {
    this.loading = true;
    const params = {
      serviceNumberId: this.serviceNumberId
    };
    this.httpService
      .post(ConstantUtil.TENANT_SERVICENUMBER_IDENTITY_V1_LIST, params)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.listOfData = res.data || [];

          // 为每个身份加载头像
          this.listOfData.forEach(identity => {
            if (identity.avatarId) {
              this.loadAvatarUrl(identity);
            }
          });
        } else {
          this.message.error(this.i18n.fanyi('servicenumber.identity.load-list-failed'));
        }
      });
  }
  // 获取身份类型文本
  getTypeText(type: string): string {
    const typeMap: { [key: string]: string } = {
      Assistant: this.i18n.fanyi('servicenumber.identity.type.assistant'),
      Boss: this.i18n.fanyi('servicenumber.identity.type.boss')
    };
    return typeMap[type] || type;
  }

  // 获取身份类型颜色
  getTypeColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      Assistant: 'blue',
      Boss: 'purple'
    };
    return colorMap[type] ?? 'default';
  }
  // 删除身份
  deleteIdentity(id: string): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('servicenumber.identity.delete-confirm-title'),
      nzContent: this.i18n.fanyi('servicenumber.identity.delete-confirm-content'),
      nzOkDanger: true,
      nzOkText: this.i18n.fanyi('common.confirm'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOkType: 'primary',
      nzOnOk: () => {
        this.httpService.post(`${ConstantUtil.TENANT_SERVICENUMBER_IDENTITY_DELETE}/${id}`, {}).subscribe(res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('servicenumber.identity.delete-success'));
            this.loadData();
          } else {
            this.message.error(this.i18n.fanyi('servicenumber.identity.delete-failed'));
          }
        });
      }
    });
  }
  addIdentity(): void {
    this.modal
      .create({
        nzTitle: this.i18n.fanyi('servicenumber.identity.create-title'),
        nzContent: IdentityDetailComponent,
        nzWidth: '800px',
        nzFooter: null,
        nzData: {
          serviceNumberId: this.serviceNumberId,
          isAdd: true
        }
      })
      .afterClose.subscribe(() => {
        this.loadData();
      });
  }

  editIdentity(id: string): void {
    this.modal
      .create({
        nzTitle: this.i18n.fanyi('servicenumber.identity.edit-title'),
        nzContent: IdentityDetailComponent,
        nzWidth: '800px',
        nzFooter: null,
        nzData: {
          serviceNumberId: this.serviceNumberId,
          identityId: id,
          isAdd: false
        }
      })
      .afterClose.subscribe(() => {
        this.loadData();
      });
  }

  // 设置默认身份
  setDefaultIdentity(id: string): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('servicenumber.identity.set-default-confirm-title'),
      nzContent: this.i18n.fanyi('servicenumber.identity.set-default-confirm-content'),
      nzOkText: this.i18n.fanyi('common.confirm'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () => {
        const params = {
          identityId: id,
          serviceNumberId: this.serviceNumberId
        };
        this.httpService.post(ConstantUtil.TENANT_SERVICENUMBER_IDENTITY_SET_DEFAULT, params).subscribe(res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('servicenumber.identity.set-default-success'));
            this.loadData(); // 重新加载数据以更新UI
          } else {
            this.message.error(res.msg ?? this.i18n.fanyi('servicenumber.identity.set-default-failed'));
          }
        });
      }
    });
  }

  loadAvatarUrl(identity: IdentityItem): void {
    this.imageService.loadImage(identity.avatarId, 'M').subscribe(imageUrl => {
      if (imageUrl) {
        identity.avatarUrl = imageUrl;
      }
      this.cdr.detectChanges();
    });
  }
}
